// «بنك الأرقام» (0104) — قرار المالك: بنكٌ لكل بياع من Cold Leads القديمة، يسحب منه
// بزرّ لغاية 20 رقم في اليوم **على دفعات من 5** (0148، قرار المالك 01/10/2026: «يسحب 5، وبعد ما يخلّصهم يسحب 5،
// وهكذا لغاية 20»)، والرقم يأتي معه تاريخُه: متى كُلِّم، وطلب إيه، ووصل لإيه.
//
// **الرقم المسحوب يبقى قدّامه اليوم حتى يقفله بمتابعة** — وعندها يختفي من هنا إلى
// الأبد ويظهر في متابعاته كأي عميل. وما لم يُكلَّم لمّا يقفل اليوم **يرجع لبنكه وحده**
// (0106) فيسحبه في يومٍ آخر — لا يضيع ولا يبقى معلَّقًا.
//
// **وكم فاضل في بنكه كلّه** (قرار المالك: «أعرف اللي سحب من أرقام فاضل كام من البنك
// بتاعه كإجمالي»): سطرٌ وشريطٌ — بنكك كله، اتكلمت فيها، فاضل.
//
// **و«سجّل متابعة» تملأ النموذج بالاسم والرقم وآخر طلب** — فلا يكتبهم مرّتين، ولا
// يغلط في رقمٍ نقله بيده.
import { useCallback, useEffect, useState } from 'react';
import { ApiError, get, post } from '../lib/api.ts';
import { Alert, Badge, Card, Empty, Spinner } from '../components/ui.tsx';
import { shortDate } from '../lib/format.ts';
import { PhoneActions } from '../components/PhoneActions.tsx';
import { BonusRule } from '../commission/LeadBankBonusCard.tsx';

const msg = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : fallback);

export interface LeadNumber {
  id: string; phone: string; customerName: string | null; category: string | null;
  firstContact: string | null; lastContact: string | null; contactCount: number;
  lastInquiry: string | null; lastStatus: string | null; history: string | null;
  pulledOn: string | null; pulledAt: string | null; followUpId: string | null;
}
export interface LeadBank {
  total: number; available: number; followedTotal: number;
  pulledToday: number; limit: number; remainingToday: number; items: LeadNumber[];
  /** الدفعة (5) وما لم يُكلَّم من مسحوب النهاردة — لا دفعة جديدة قبل أن يصير صفرًا (0148) */
  batch?: number; openToday?: number;
}
export interface LeadPrefill { customerName: string; phone: string; notes: string }

export function LeadBankCard({ onUse }: { onUse: (p: LeadPrefill) => void }) {
  const [bank, setBank] = useState<LeadBank | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [howTo, setHowTo] = useState(false);
  /** سياسة المكافأة (0107) — تُعرض هنا أيضًا كي تُقرأ حيث يُسحب الرقم؛ وغيابها لا يعطّل البطاقة */
  const [bonus, setBonus] = useState<{ threshold: string; underAmount: string; overAmount: string } | null>(null);
  useEffect(() => {
    get<{ policy: { threshold: string; underAmount: string; overAmount: string } | null }>('/me/lead-bank-bonus')
      .then(r => setBonus(r.policy)).catch(() => setBonus(null));
  }, []);

  const load = useCallback(async () => {
    try { setBank(await get<LeadBank>('/me/lead-bank')); }
    catch (e) { setErr(msg(e, 'تعذّر تحميل بنك الأرقام')); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function pull() {
    setErr(null); setFlash(null); setBusy(true);
    try {
      const r = await post<{ pulled: number }>('/me/lead-bank/pull', {});
      setFlash(`اتسحب ${r.pulled} رقم — ابدأ بيهم النهاردة.`);
      await load();
    } catch (e) { setErr(msg(e, 'تعذّر السحب')); }
    finally { setBusy(false); }
  }

  if (bank === null && !err) return <Card title="بنك الأرقام"><Spinner /></Card>;

  const pending = (bank?.items ?? []).filter(n => !n.followUpId);
  const done = (bank?.items ?? []).filter(n => n.followUpId);

  return (
    <Card title="بنك الأرقام" action={
      bank && (
        <button type="button" className="btn-primary px-4 py-2 text-sm"
          disabled={busy || bank.remainingToday === 0 || bank.available === 0 || (bank.openToday ?? 0) > 0}
          onClick={() => void pull()}>
          {busy ? 'جارٍ السحب…'
            : bank.remainingToday === 0 ? `خلّصت الـ${bank.limit} بتوع النهاردة`
            : bank.available === 0 ? 'بنكك فاضي'
            : (bank.openToday ?? 0) > 0 ? `خلّص الـ${bank.openToday} اللي معاك الأول`
            : `اسحب ${Math.min(bank.batch ?? bank.remainingToday, bank.remainingToday, bank.available)} رقم`}
        </button>
      )
    }>
      {err && <Alert>{err}</Alert>}
      {flash && <Alert kind="success">{flash}</Alert>}
      {bank && (
        <>
          {/* من فين الأرقام دي؟ (قرار المالك: «كل واحد يعرف إن بنك الأرقام من الأرقام اللي سجّلها بنفسه على شيته — مش أرقام Sales تانية») */}
          <div className="mb-3 rounded-lg bg-sky-50 p-3 text-sm leading-6 text-sky-900">
            <b>الأرقام دي بتاعتك أنت</b> — عملاء كلّمتهم وسجّلتهم بنفسك، ومعاهم تاريخ كل مكالمة وطلبها.
            <br />اللي تسجّل بيه متابعة <b>ما بيرجعش هنا</b>، واللي ما تكلّموش <b>يرجع لبنكك آخر اليوم</b>.
          </div>
          <button type="button" onClick={() => setHowTo(v => !v)}
            className={`mb-3 w-full rounded-xl border-2 px-4 py-3 text-start text-sm font-bold transition-colors ${
              howTo ? 'border-emerald-700 bg-emerald-700 text-white' : 'border-emerald-600 bg-emerald-600 text-white shadow-md hover:bg-emerald-700'}`}>
            📞 إزاي أكلّم العملاء دي وأفتح معاهم كلام وشغل؟ — ١٠ سيناريوهات جاهزة {howTo ? '▴ إخفاء' : '▾ افتح'}
          </button>
          {howTo && <HowToTalk />}
          {bonus && <BonusRule policy={bonus} compact />}
          <BankProgress bank={bank} />
          <div className="mb-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-ink-600">
            <span>سحبت النهاردة: <b className="tabular-nums text-ink-900">{bank.pulledToday}</b> من {bank.limit}</span>
            {bank.batch && <span>بتسحب <b>{bank.batch}</b> في المرّة — سجّل متابعة لكلّهم (ولو «مردّش») وتسحب اللي بعدهم</span>}
            {pending.length > 0 && <span className="text-amber-700">لسه ما اتكلمتش: <b className="tabular-nums">{pending.length}</b></span>}
          </div>

          {pending.length === 0 ? (
            <Empty>{bank.available === 0 ? 'بنكك فاضي — كل أرقامك اتسحبت.' : 'اضغط «اسحب» وهتلاقي الأرقام هنا بتاريخها وطلبها.'}</Empty>
          ) : (
            <ul className="divide-y divide-slate-100">
              {pending.map(n => (
                <li key={n.id} className="py-2.5">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <b className="text-ink-900">{n.customerName || 'بدون اسم'}</b>
                        <span dir="ltr" className="font-mono text-sm text-ink-700"><PhoneActions phone={n.phone} /></span>
                        {n.category && <Badge tone="bg-slate-100 text-ink-600">{n.category}</Badge>}
                        {n.pulledOn && n.pulledOn !== (bank.items[0]?.pulledOn ?? '') && (
                          <Badge tone="bg-amber-100 text-amber-900">من {shortDate(n.pulledOn)}</Badge>
                        )}
                      </div>
                      <div className="mt-0.5 text-xs text-ink-600">
                        آخر تواصل <b className="tabular-nums">{n.lastContact ? shortDate(n.lastContact) : '؟'}</b>
                        {' · '}اتكلم <b className="tabular-nums">{n.contactCount}</b> مرّة
                        {n.lastStatus && <> · <span className="text-ink-700">{n.lastStatus}</span></>}
                      </div>
                      {n.lastInquiry && <div className="mt-0.5 text-sm text-ink-800">طلب: {n.lastInquiry}</div>}
                      {n.history && n.contactCount > 1 && (
                        <button type="button" className="mt-1 text-xs font-semibold text-brand-700 hover:underline"
                          onClick={() => setOpen(open === n.id ? null : n.id)}>
                          {open === n.id ? 'إخفاء السجل' : `كل المكالمات (${n.contactCount})`}
                        </button>
                      )}
                      {open === n.id && n.history && (
                        <pre className="mt-1 max-h-48 overflow-y-auto whitespace-pre-wrap rounded-lg bg-slate-50 p-2 text-xs leading-5 text-ink-700">{n.history}</pre>
                      )}
                    </div>
                    <button type="button" className="btn-ghost shrink-0 px-3 py-1.5 text-sm"
                      onClick={() => onUse({ customerName: n.customerName ?? '', phone: n.phone, notes: n.lastInquiry ? `طلب قبل كده: ${n.lastInquiry}` : '' })}>
                      سجّل متابعة
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {done.length > 0 && (
            <p className="mt-2 text-xs text-emerald-800">اتكلمت النهاردة مع {done.length} من الأرقام المسحوبة ✔</p>
          )}
        </>
      )}
    </Card>
  );
}

/** بنكك كله · اتكلمت فيها · فاضل — رقمًا وشريطًا، فيعرف البياع أين هو من بنكه كلّه. */
function BankProgress({ bank }: { bank: LeadBank }) {
  const pct = bank.total > 0 ? Math.round((bank.followedTotal / bank.total) * 100) : 0;
  return (
    <div className="mb-3 rounded-lg border border-slate-200 p-3">
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-ink-600">
        <span>بنكك كله: <b className="tabular-nums text-ink-900">{bank.total.toLocaleString('en')}</b> رقم</span>
        <span>اتكلمت فيها: <b className="tabular-nums text-emerald-800">{bank.followedTotal.toLocaleString('en')}</b></span>
        <span>فاضل في بنكك: <b className="tabular-nums text-ink-900">{bank.available.toLocaleString('en')}</b></span>
      </div>
      <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100" role="progressbar"
        aria-label="نسبة ما اتكلمت فيه من بنكك" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-1 text-xs text-ink-500">خلّصت {pct}٪ من بنكك.</div>
    </div>
  );
}

/**
 * **عشرة سيناريوهات لفتح الكلام** (قرار المالك: «عرّفهم ٥ سيناريو إزاي يكلموا العملاء دي
 * ويفتحوا كلام معاهم وشغل» ثم «وضّح دي ليهم وزوّد ٥ سيناريو كمان») — كلٌّ منها يبدأ من **الطلب القديم المكتوب قدّامه**، لأن
 * عميلًا يسمع إنك فاكر طلبه يفتح لك قبل ما تعرض حاجة.
 */
function HowToTalk() {
  const S: { title: string; when: string; open: string; then: string }[] = [
    { title: '١. الرجوع بالطلب القديم',
      when: 'أكتر حالة: العميل سأل عن رحلة أو خدمة وما حجزش.',
      open: '«أهلًا يا فندم، أنا [اسمك] من iConTravel. حضرتك كنت سألتنا يوم [التاريخ] عن [الطلب القديم] — حابب أطمّن: الرحلة دي تمّت ولا لسه في النية؟»',
      then: 'لو تمّت: «تمام، لو في سفر جاي ابقى قولّي وأنا أجيبلك أحسن سعر». لو لسه: اعرض سعر النهاردة فورًا واسأل عن الموعد.' },
    { title: '٢. عرض جديد على نفس الوجهة',
      when: 'لما يكون عندك سعر أو رحلة أحسن على الوجهة اللي كان بيسأل عنها.',
      open: '«حضرتك كنت بتسأل عن [الوجهة] — نزل عندنا عرض النهاردة على نفس الخط بسعر [السعر]، قولت أبلّغك قبل ما يخلص.»',
      then: 'حدّد مدّة للعرض بصدق، واطلب بيانات المسافرين لو وافق مبدئيًّا.' },
    { title: '٣. المناسبة أو الموسم',
      when: 'قبل الإجازات، العمرة، رمضان، الصيف، بداية الدراسة.',
      open: '«بما إن [الموسم] قرّب، وحضرتك كنت مهتم بـ[الطلب القديم]، حابب أشوف لحضرتك حاجة مناسبة قبل ما الأسعار تعلى؟»',
      then: 'اسأل عن عدد الأفراد والتواريخ التقريبية — وسجّل الإجابة في المتابعة حتى لو ما حجزش.' },
    { title: '٤. الخدمة المكمّلة',
      when: 'العميل حجز أو سأل عن شيء وله تكملة: طيران ← فندق/تأشيرة، تأشيرة ← طيران.',
      open: '«حضرتك أخدت/سألت عن [الخدمة] — محتاج معاها [الخدمة المكمّلة]؟ عندنا [مثال] بسعر كويس ونخلّصهالك مع بعض.»',
      then: 'لو رفض بأدب: «تمام، لو احتجت في أي وقت أنا موجود» — وحدّد متابعة بعد شهر.' },
    { title: '٥. اللي ما ردّش قبل كده',
      when: 'حالته «لم يرد» أو «هيرجع يرد» ومر وقت طويل.',
      open: 'رسالة واتساب قصيرة أوّلًا: «أهلًا يا فندم، [اسمك] من iConTravel — كنت سألتنا عن [الطلب] يوم [التاريخ]. لو لسه محتاج، ابعتلي أنسب وقت أكلّمك فيه.» ثم اتصال بعد ساعة لو قرا وما ردّش.',
      then: 'ما تتصلش أكتر من مرّتين في اليوم. لو ما ردّ خالص سجّل «لم يرد» — البرنامج بيعدّها متابعة.' },
    { title: '٦. عميل حجز معانا قبل كده',
      when: 'السجل بيقول إنه اتحوّل لحجز أو حالته «حجز» — عميل بيعرفنا.',
      open: '«أهلًا يا فندم، [اسمك] من iConTravel — حضرتك سافرت معانا [الوجهة] يوم [التاريخ]، الرحلة كانت مريحة؟ بنتصل بعملاءنا القدام الأول لما يبقى عندنا عروض.»',
      then: 'اسمع تعليقه للآخر، ثم: «في سفر جاي في بالك؟» — العميل القديم أسهل عميل.' },
    { title: '٧. قال إن السعر غالي',
      when: 'الحالة فيها «غالي» أو «مش عاجبه السعر» أو «هيشوف».',
      open: '«حضرتك كنت شايف سعر [الطلب] عالي — عندي دلوقتي بديل: نفس الوجهة بتاريخ [تاريخ أرخص] بـ[السعر]، أو نفس التاريخ بترانزيت أرخص. أيهما أنسب؟»',
      then: 'قدّم خيارين مش خيار واحد — الاختيار بين اتنين أسهل من «نعم/لا».' },
    { title: '٨. كان بيستنى تأشيرة أو جواز',
      when: 'الحالة فيها «مستني التأشيرة» أو «الجواز» أو «الدعوة».',
      open: '«حضرتك كنت مستني [التأشيرة/الجواز] عشان [الوجهة] — طلعت؟ لو طلعت أحجزلك دلوقتي قبل ما الأسعار تتحرك، ولو لسه أقدر أساعدك في الإجراءات.»',
      then: 'لو ما طلعتش: حدّد متابعة بعد أسبوعين واكتب السبب — الرقم ده هيحجز يوم ما تطلع.' },
    { title: '٩. كلّمنا على الصفحة أو واتساب وما كمّلش',
      when: 'القناة «رسالة صفحة» أو واتساب والحالة «جديد» بس.',
      open: 'رسالة: «أهلًا يا فندم 🌹 [اسمك] من iConTravel. حضرتك بعتلنا على الصفحة يوم [التاريخ] بخصوص [الطلب] — لو لسه محتاج ابعتلي التاريخ وعدد الأفراد وأرد عليك بسعر النهاردة خلال دقايق.»',
      then: 'ردّ في نفس اليوم بسعر مكتوب وواضح — العميل اللي كتب رسالة بيفضّل الرقم مكتوب قدّامه.' },
    { title: '١٠. عائلة أو مجموعة',
      when: 'الطلب فيه أفراد أو أطفال أو أكتر من راكب.',
      open: '«حضرتك كنت بتسأل لـ[العدد] أفراد على [الوجهة] — عندنا دلوقتي سعر مجموعة أحسن من الفردي، ومع الأطفال في تخفيض. أحسبلك التكلفة الكاملة؟»',
      then: 'اسأل عن الفندق والانتقالات — المجموعة بتاخد الباقة كاملة لو الرقم واحد وواضح.' },
  ];
  return (
    <ol className="mb-3 space-y-2">
      {S.map(x => (
        <li key={x.title} className="rounded-lg border border-slate-200 p-3 text-sm">
          <div className="font-semibold text-ink-900">{x.title}</div>
          <div className="text-xs text-ink-500">امتى: {x.when}</div>
          <div className="mt-1 text-ink-800">{x.open}</div>
          <div className="mt-1 text-xs text-ink-600">وبعدها: {x.then}</div>
        </li>
      ))}
      <li className="text-xs text-ink-500">
        قاعدة عامّة: ابدأ دايمًا بطلبه القديم اللي مكتوب قدّامك — العميل لما يحسّ إنك فاكره بيفتح لك. وسجّل كل مكالمة
        في «تسجيل متابعة» حتى لو ما ردّش، عشان تتحسب في تارجت اليوم.
      </li>
    </ol>
  );
}
